import React, { useState } from 'react';
import { MDBIcon } from 'mdbreact';

const ConstructionCostDashboard = ({ item, useDemoData = true }) => {
  const [expandedSections, setExpandedSections] = useState({});

  // Demo catalog data - full dataset from original snippet
  const catalogData = [
    {
      id: 1,
      code: "A",
      description: "Substructure - Concrete Foundation",
      unitCost: 220.00,
      unit: "SF",
      category: "Foundation",
      location: { city: "Tempe", state: "Arizona" },
      projectType: "Restaurant",
      laborType: "Union",
      costBreakdown: {
        rawMaterial: 45.00,
        manufacturing: 28.00,
        transportation: 12.00,
        distributorIssue: 18.50,
        contractorIssue: 35.20,
        laborCost: 81.30,
        equipmentCost: 15.00
      },
      supplyChain: [
        {
          stage: "Raw Materials",
          entity: "Cement Producer",
          cost: 45.00,
          issue: 0,
          sources: ["Portland Cement Assoc", "USGS Minerals"],
          confidence: "high"
        },
        {
          stage: "Manufacturing",
          entity: "Ready-Mix Plant",
          cost: 28.00,
          issue: 15,
          sources: ["NRMCA Data", "Regional Suppliers"],
          confidence: "high"
        },
        {
          stage: "Transportation",
          entity: "Freight/Delivery",
          cost: 12.00,
          issue: 8,
          sources: ["Fuel Index", "Distance Calculation"],
          confidence: "medium"
        },
        {
          stage: "Distribution",
          entity: "Home Depot / Lowes",
          cost: 18.50,
          issue: 22,
          sources: ["Supplier Quote", "Market Average"],
          confidence: "high"
        },
        {
          stage: "Equipment",
          entity: "Construction Equipment Rental",
          cost: 15.00,
          issue: 0,
          sources: ["Equipment Rental Rates", "Industry Average"],
          confidence: "high",
          details: [
            { code: "17.21", description: "Power Trowel", cost: 3.50 },
            { code: "45.4", description: "Concrete Mixer", cost: 4.20 },
            { code: "86.11", description: "Vibrator", cost: 2.80 },
            { code: "17.10", description: "Concrete Pump", cost: 4.50 }
          ]
        },
        {
          stage: "Contractor",
          entity: "General Contractor",
          cost: 35.20,
          issue: 18,
          sources: ["Industry Standard", "Historical Data"],
          confidence: "medium"
        },
        {
          stage: "Labor",
          entity: "Union Workers",
          cost: 81.30,
          issue: 0,
          sources: ["BLS Wage Data", "Union Rate Sheet"],
          confidence: "high",
          details: [
            { code: "111", description: "General Labor", cost: 35.00, percentage: 43 },
            { code: "112", description: "Skilled Labor", cost: 28.50, percentage: 35 },
            { code: "992", description: "Site Foreman", cost: 12.80, percentage: 16 },
            { code: "213", description: "Equipment Operator", cost: 5.00, percentage: 6 }
          ]
        }
      ],
      historicalTrend: [
        { date: "2023-Q4", price: 198.00, events: [] },
        { date: "2024-Q1", price: 210.00, events: [{ type: "negative", label: "Trump Steel Tariffs 25%", impact: "+6.1%" }] },
        { date: "2024-Q2", price: 215.00, events: [] },
        { date: "2024-Q3", price: 218.00, events: [] },
        { date: "2024-Q4", price: 220.00, events: [{ type: "warning", label: "Cement plant maintenance", impact: "+0.9%" }], isToday: true }
      ],
      projections: [
        { 
          date: "2025-Q1", 
          optimistic: 225.00, 
          realistic: 228.00, 
          pessimistic: 235.00,
          scenarios: [
            { condition: "Tariffs remain in place", probability: "70%", impact: "+3-5%" },
            { condition: "New EPA cement regulations", probability: "45%", impact: "+2-4%" }
          ]
        },
        { 
          date: "2025-Q2", 
          optimistic: 228.00, 
          realistic: 235.00, 
          pessimistic: 245.00,
          scenarios: [
            { condition: "Labor shortage continues", probability: "80%", impact: "+3-6%" }
          ]
        },
        { 
          date: "2025-Q3", 
          optimistic: 230.00, 
          realistic: 240.00, 
          pessimistic: 255.00,
          scenarios: [
            { condition: "Construction boom continues", probability: "75%", impact: "+4-8%" }
          ]
        },
        { 
          date: "2025-Q4", 
          optimistic: 232.00, 
          realistic: 245.00, 
          pessimistic: 265.00,
          scenarios: [
            { condition: "Winter demand reduction", probability: "85%", impact: "-1-2%" }
          ]
        }
      ],
      componentTrends: {
        rawMaterial: [
          { date: "Dec '23", price: 41.50, isHistory: true },
          { date: "Jan '24", price: 42.00, isHistory: true },
          { date: "Feb '24", price: 42.50, isHistory: true },
          { date: "Mar '24", price: 45.00, isHistory: true, event: "Limestone shortage" },
          { date: "Apr '24", price: 44.80, isHistory: true },
          { date: "May '24", price: 44.70, isHistory: true },
          { date: "Jun '24", price: 44.75, isHistory: true },
          { date: "Jul '24", price: 44.85, isHistory: true },
          { date: "Aug '24", price: 44.90, isHistory: true },
          { date: "Sep '24", price: 44.95, isHistory: true },
          { date: "Oct '24", price: 45.00, isHistory: true },
          { date: "Nov '24", price: 45.00, isHistory: true, isToday: true },
          { date: "Dec '24", price: 45.50, isHistory: false },
          { date: "Jan '25", price: 46.00, isHistory: false },
          { date: "Feb '25", price: 46.50, isHistory: false },
          { date: "Mar '25", price: 47.00, isHistory: false },
          { date: "Apr '25", price: 47.50, isHistory: false },
          { date: "May '25", price: 48.00, isHistory: false }
        ],
        equipment: [
          { date: "Dec '23", price: 13.50, isHistory: true },
          { date: "Jan '24", price: 13.80, isHistory: true },
          { date: "Feb '24", price: 14.00, isHistory: true },
          { date: "Mar '24", price: 14.20, isHistory: true },
          { date: "Apr '24", price: 14.40, isHistory: true },
          { date: "May '24", price: 14.50, isHistory: true },
          { date: "Jun '24", price: 14.60, isHistory: true },
          { date: "Jul '24", price: 14.70, isHistory: true },
          { date: "Aug '24", price: 14.80, isHistory: true },
          { date: "Sep '24", price: 14.90, isHistory: true },
          { date: "Oct '24", price: 14.95, isHistory: true },
          { date: "Nov '24", price: 15.00, isHistory: true, isToday: true },
          { date: "Dec '24", price: 15.20, isHistory: false },
          { date: "Jan '25", price: 15.40, isHistory: false },
          { date: "Feb '25", price: 15.60, isHistory: false },
          { date: "Mar '25", price: 15.80, isHistory: false },
          { date: "Apr '25", price: 16.00, isHistory: false },
          { date: "May '25", price: 16.20, isHistory: false }
        ],
        labor: [
          { date: "Dec '23", price: 78.50, isHistory: true },
          { date: "Jan '24", price: 79.00, isHistory: true },
          { date: "Feb '24", price: 79.20, isHistory: true },
          { date: "Mar '24", price: 79.50, isHistory: true, event: "Union wage increase" },
          { date: "Apr '24", price: 80.00, isHistory: true },
          { date: "May '24", price: 80.20, isHistory: true },
          { date: "Jun '24", price: 80.40, isHistory: true },
          { date: "Jul '24", price: 80.60, isHistory: true },
          { date: "Aug '24", price: 80.80, isHistory: true },
          { date: "Sep '24", price: 81.00, isHistory: true },
          { date: "Oct '24", price: 81.20, isHistory: true },
          { date: "Nov '24", price: 81.30, isHistory: true, isToday: true },
          { date: "Dec '24", price: 82.00, isHistory: false },
          { date: "Jan '25", price: 82.50, isHistory: false },
          { date: "Feb '25", price: 83.00, isHistory: false },
          { date: "Mar '25", price: 83.50, isHistory: false },
          { date: "Apr '25", price: 84.00, isHistory: false },
          { date: "May '25", price: 84.50, isHistory: false }
        ],
        transportation: [
          { date: "Dec '23", price: 11.20, isHistory: true },
          { date: "Jan '24", price: 11.40, isHistory: true },
          { date: "Feb '24", price: 11.60, isHistory: true },
          { date: "Mar '24", price: 12.00, isHistory: true, event: "Oil price spike" },
          { date: "Apr '24", price: 11.90, isHistory: true },
          { date: "May '24", price: 11.85, isHistory: true },
          { date: "Jun '24", price: 11.88, isHistory: true },
          { date: "Jul '24", price: 11.92, isHistory: true },
          { date: "Aug '24", price: 11.95, isHistory: true },
          { date: "Sep '24", price: 11.97, isHistory: true },
          { date: "Oct '24", price: 12.00, isHistory: true },
          { date: "Nov '24", price: 12.00, isHistory: true, isToday: true },
          { date: "Dec '24", price: 12.20, isHistory: false },
          { date: "Jan '25", price: 12.40, isHistory: false },
          { date: "Feb '25", price: 12.60, isHistory: false },
          { date: "Mar '25", price: 12.80, isHistory: false },
          { date: "Apr '25", price: 13.00, isHistory: false },
          { date: "May '25", price: 13.20, isHistory: false }
        ],
        distribution: [
          { date: "Dec '23", price: 17.50, isHistory: true },
          { date: "Jan '24", price: 17.70, isHistory: true },
          { date: "Feb '24", price: 17.90, isHistory: true },
          { date: "Mar '24", price: 18.00, isHistory: true },
          { date: "Apr '24", price: 18.10, isHistory: true },
          { date: "May '24", price: 18.20, isHistory: true },
          { date: "Jun '24", price: 18.30, isHistory: true },
          { date: "Jul '24", price: 18.35, isHistory: true },
          { date: "Aug '24", price: 18.40, isHistory: true },
          { date: "Sep '24", price: 18.45, isHistory: true },
          { date: "Oct '24", price: 18.50, isHistory: true },
          { date: "Nov '24", price: 18.50, isHistory: true, isToday: true },
          { date: "Dec '24", price: 18.70, isHistory: false },
          { date: "Jan '25", price: 18.90, isHistory: false },
          { date: "Feb '25", price: 19.10, isHistory: false },
          { date: "Mar '25", price: 19.30, isHistory: false },
          { date: "Apr '25", price: 19.50, isHistory: false },
          { date: "May '25", price: 19.70, isHistory: false }
        ],
        contractor: [
          { date: "Dec '23", price: 33.00, isHistory: true },
          { date: "Jan '24", price: 33.50, isHistory: true },
          { date: "Feb '24", price: 34.00, isHistory: true },
          { date: "Mar '24", price: 34.50, isHistory: true },
          { date: "Apr '24", price: 34.80, isHistory: true },
          { date: "May '24", price: 35.00, isHistory: true },
          { date: "Jun '24", price: 35.10, isHistory: true },
          { date: "Jul '24", price: 35.15, isHistory: true },
          { date: "Aug '24", price: 35.18, isHistory: true },
          { date: "Sep '24", price: 35.19, isHistory: true },
          { date: "Oct '24", price: 35.20, isHistory: true },
          { date: "Nov '24", price: 35.20, isHistory: true, isToday: true },
          { date: "Dec '24", price: 35.50, isHistory: false },
          { date: "Jan '25", price: 36.00, isHistory: false },
          { date: "Feb '25", price: 36.50, isHistory: false },
          { date: "Mar '25", price: 37.00, isHistory: false },
          { date: "Apr '25", price: 37.50, isHistory: false },
          { date: "May '25", price: 38.00, isHistory: false }
        ]
      }
    },
    {
      id: 2,
      code: "B",
      description: "Shell - Exterior Masonry",
      unitCost: 22.00,
      unit: "LF",
      category: "Exterior",
      location: { city: "Tempe", state: "Arizona" },
      projectType: "Restaurant",
      laborType: "Union",
      costBreakdown: {
        rawMaterial: 4.80,
        manufacturing: 3.20,
        transportation: 1.50,
        distributorIssue: 2.10,
        contractorIssue: 3.80,
        laborCost: 6.60,
        equipmentCost: 0
      },
      supplyChain: [
        {
          stage: "Raw Materials",
          entity: "Clay/Aggregate Supplier",
          cost: 4.80,
          issue: 0,
          sources: ["Mining Report", "Commodity Index"],
          confidence: "high"
        },
        {
          stage: "Manufacturing",
          entity: "Brick Manufacturer",
          cost: 3.20,
          issue: 18,
          sources: ["Manufacturer Direct"],
          confidence: "high"
        },
        {
          stage: "Transportation",
          entity: "Regional Freight",
          cost: 1.50,
          issue: 10,
          sources: ["Freight Quote"],
          confidence: "medium"
        },
        {
          stage: "Distribution",
          entity: "Building Supply",
          cost: 2.10,
          issue: 25,
          sources: ["Distributor Price List"],
          confidence: "high"
        },
        {
          stage: "Contractor",
          entity: "Masonry Contractor",
          cost: 3.80,
          issue: 20,
          sources: ["Market Rate"],
          confidence: "medium"
        },
        {
          stage: "Labor",
          entity: "Union Masons",
          cost: 6.60,
          issue: 0,
          sources: ["Union Scale", "BLS Data"],
          confidence: "high"
        }
      ],
      historicalTrend: [
        { date: "2023-Q4", price: 20.00, events: [] },
        { date: "2024-Q1", price: 20.50, events: [] },
        { date: "2024-Q2", price: 21.00, events: [] },
        { date: "2024-Q3", price: 21.50, events: [] },
        { date: "2024-Q4", price: 22.00, events: [], isToday: true }
      ],
      projections: [
        { 
          date: "2025-Q1", 
          optimistic: 22.50, 
          realistic: 23.00, 
          pessimistic: 24.00,
          scenarios: [
            { condition: "Clay prices stable", probability: "65%", impact: "+2-4%" },
            { condition: "Labor costs increase", probability: "70%", impact: "+3%" }
          ]
        },
        { 
          date: "2025-Q2", 
          optimistic: 23.00, 
          realistic: 23.80, 
          pessimistic: 25.00,
          scenarios: [
            { condition: "Increased demand", probability: "60%", impact: "+4%" }
          ]
        },
        { 
          date: "2025-Q3", 
          optimistic: 23.50, 
          realistic: 24.50, 
          pessimistic: 26.00,
          scenarios: []
        },
        { 
          date: "2025-Q4", 
          optimistic: 24.00, 
          realistic: 25.00, 
          pessimistic: 27.00,
          scenarios: []
        }
      ],
      componentTrends: {
        rawMaterial: [
          { date: "Dec '23", price: 4.50, isHistory: true },
          { date: "Jan '24", price: 4.55, isHistory: true },
          { date: "Feb '24", price: 4.60, isHistory: true },
          { date: "Mar '24", price: 4.65, isHistory: true },
          { date: "Apr '24", price: 4.68, isHistory: true },
          { date: "May '24", price: 4.70, isHistory: true },
          { date: "Jun '24", price: 4.72, isHistory: true },
          { date: "Jul '24", price: 4.74, isHistory: true },
          { date: "Aug '24", price: 4.76, isHistory: true },
          { date: "Sep '24", price: 4.78, isHistory: true },
          { date: "Oct '24", price: 4.79, isHistory: true },
          { date: "Nov '24", price: 4.80, isHistory: true, isToday: true },
          { date: "Dec '24", price: 4.90, isHistory: false },
          { date: "Jan '25", price: 5.00, isHistory: false },
          { date: "Feb '25", price: 5.10, isHistory: false },
          { date: "Mar '25", price: 5.20, isHistory: false },
          { date: "Apr '25", price: 5.30, isHistory: false },
          { date: "May '25", price: 5.40, isHistory: false }
        ],
        labor: [
          { date: "Dec '23", price: 6.00, isHistory: true },
          { date: "Jan '24", price: 6.10, isHistory: true },
          { date: "Feb '24", price: 6.15, isHistory: true },
          { date: "Mar '24", price: 6.20, isHistory: true },
          { date: "Apr '24", price: 6.30, isHistory: true },
          { date: "May '24", price: 6.35, isHistory: true },
          { date: "Jun '24", price: 6.40, isHistory: true },
          { date: "Jul '24", price: 6.45, isHistory: true },
          { date: "Aug '24", price: 6.50, isHistory: true },
          { date: "Sep '24", price: 6.55, isHistory: true },
          { date: "Oct '24", price: 6.58, isHistory: true },
          { date: "Nov '24", price: 6.60, isHistory: true, isToday: true },
          { date: "Dec '24", price: 6.70, isHistory: false },
          { date: "Jan '25", price: 6.80, isHistory: false },
          { date: "Feb '25", price: 6.90, isHistory: false },
          { date: "Mar '25", price: 7.00, isHistory: false },
          { date: "Apr '25", price: 7.10, isHistory: false },
          { date: "May '25", price: 7.20, isHistory: false }
        ],
        transportation: [
          { date: "Dec '23", price: 1.35, isHistory: true },
          { date: "Jan '24", price: 1.38, isHistory: true },
          { date: "Feb '24", price: 1.40, isHistory: true },
          { date: "Mar '24", price: 1.42, isHistory: true },
          { date: "Apr '24", price: 1.44, isHistory: true },
          { date: "May '24", price: 1.46, isHistory: true },
          { date: "Jun '24", price: 1.47, isHistory: true },
          { date: "Jul '24", price: 1.48, isHistory: true },
          { date: "Aug '24", price: 1.49, isHistory: true },
          { date: "Sep '24", price: 1.49, isHistory: true },
          { date: "Oct '24", price: 1.50, isHistory: true },
          { date: "Nov '24", price: 1.50, isHistory: true, isToday: true },
          { date: "Dec '24", price: 1.55, isHistory: false },
          { date: "Jan '25", price: 1.60, isHistory: false },
          { date: "Feb '25", price: 1.65, isHistory: false },
          { date: "Mar '25", price: 1.70, isHistory: false },
          { date: "Apr '25", price: 1.75, isHistory: false },
          { date: "May '25", price: 1.80, isHistory: false }
        ],
        distribution: [
          { date: "Dec '23", price: 1.95, isHistory: true },
          { date: "Jan '24", price: 1.98, isHistory: true },
          { date: "Feb '24", price: 2.00, isHistory: true },
          { date: "Mar '24", price: 2.02, isHistory: true },
          { date: "Apr '24", price: 2.04, isHistory: true },
          { date: "May '24", price: 2.06, isHistory: true },
          { date: "Jun '24", price: 2.07, isHistory: true },
          { date: "Jul '24", price: 2.08, isHistory: true },
          { date: "Aug '24", price: 2.09, isHistory: true },
          { date: "Sep '24", price: 2.09, isHistory: true },
          { date: "Oct '24", price: 2.10, isHistory: true },
          { date: "Nov '24", price: 2.10, isHistory: true, isToday: true },
          { date: "Dec '24", price: 2.15, isHistory: false },
          { date: "Jan '25", price: 2.20, isHistory: false },
          { date: "Feb '25", price: 2.25, isHistory: false },
          { date: "Mar '25", price: 2.30, isHistory: false },
          { date: "Apr '25", price: 2.35, isHistory: false },
          { date: "May '25", price: 2.40, isHistory: false }
        ],
        contractor: [
          { date: "Dec '23", price: 3.50, isHistory: true },
          { date: "Jan '24", price: 3.55, isHistory: true },
          { date: "Feb '24", price: 3.60, isHistory: true },
          { date: "Mar '24", price: 3.65, isHistory: true },
          { date: "Apr '24", price: 3.70, isHistory: true },
          { date: "May '24", price: 3.73, isHistory: true },
          { date: "Jun '24", price: 3.75, isHistory: true },
          { date: "Jul '24", price: 3.77, isHistory: true },
          { date: "Aug '24", price: 3.78, isHistory: true },
          { date: "Sep '24", price: 3.79, isHistory: true },
          { date: "Oct '24", price: 3.80, isHistory: true },
          { date: "Nov '24", price: 3.80, isHistory: true, isToday: true },
          { date: "Dec '24", price: 3.90, isHistory: false },
          { date: "Jan '25", price: 4.00, isHistory: false },
          { date: "Feb '25", price: 4.10, isHistory: false },
          { date: "Mar '25", price: 4.20, isHistory: false },
          { date: "Apr '25", price: 4.30, isHistory: false },
          { date: "May '25", price: 4.40, isHistory: false }
        ]
      }
    }
  ];

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const getStageIcon = (stage) => {
    const iconMap = {
      "Raw Materials": "cube",
      "Manufacturing": "industry",
      "Transportation": "truck",
      "Distribution": "store",
      "Equipment": "wrench",
      "Contractor": "building",
      "Labor": "hard-hat"
    };
    return iconMap[stage] || "dollar-sign";
  };

  const getConfidenceBadge = (confidence) => {
    const colors = {
      high: "bg-success text-white",
      medium: "bg-warning text-dark",
      low: "bg-danger text-white"
    };
    return (
      <span className={`badge ${colors[confidence]} rounded-pill px-3 py-1`}>
        {confidence} confidence
      </span>
    );
  };

  // Adapt a real CurLight catalog tree-node into the shape this
  // dashboard renders. The catalog response exposes real component costs
  // (materials / crew-labor / equipment) and a composed total, but has
  // NO manufacturing/transportation/distribution/contractor split and no
  // per-code history or projections — so we populate the buckets we
  // genuinely have and leave the rest empty (the render guards hide
  // them) rather than showing fabricated numbers.
  const adaptRealItem = (it) => {
    if (!it) return null;
    const num = (v) => (typeof v === "number" && !isNaN(v) ? v : 0);
    const material = num(it.materialUnitCost ?? it.Database?.MaterialUnitCost);
    const labor = num(it.crewUnitCost ?? it.Database?.CrewUnitCost);
    const equipment = num(
      it.equipmentUnitCost ?? it.Database?.EquipmentUnitCost
    );
    const composed = num(
      it.totalComposedUnitCost ?? it.Database?.TotalComposedUnitCost
    );
    const unitCost =
      num(it.unitCost ?? it.Database?.UnitCost) ||
      composed ||
      material + labor + equipment;
    const locked = Boolean(it.locked || it.Database?.locked);

    // Real supply-chain stages from the three components the catalog
    // actually carries. Only include stages with a value so we never
    // render an empty/zero stage.
    const supplyChain = [
      {
        stage: "Raw Materials",
        entity: "Material Suppliers",
        cost: material,
        issue: 0,
        sources: ["CurLight catalog — materials"],
        confidence: material > 0 ? "high" : "low",
      },
      {
        stage: "Labor",
        entity: "Crews",
        cost: labor,
        issue: 0,
        sources: ["CurLight catalog — crews / labor rates"],
        confidence: labor > 0 ? "high" : "low",
      },
      {
        stage: "Equipment",
        entity: "Equipment / Tools",
        cost: equipment,
        issue: 0,
        sources: ["CurLight catalog — equipment"],
        confidence: equipment > 0 ? "high" : "low",
      },
    ].filter((s) => s.cost > 0);

    return {
      id: it._id,
      code: it.Division || it.Database?.Code || "N/A",
      description: it.Description || it.Database?.Description || "Catalog item",
      unitCost: locked ? 0 : unitCost,
      unit: it.Unit || it.Database?.Unit || "unit",
      category: it.category || "Uniformat",
      location: { city: it.City || "Selected location", state: it.State || "" },
      projectType: it.ProjectType || "—",
      laborType: it.LaborType || "—",
      locked,
      costBreakdown: {
        rawMaterial: locked ? 0 : material,
        manufacturing: 0,
        transportation: 0,
        distributorIssue: 0,
        equipmentCost: locked ? 0 : equipment,
        laborCost: locked ? 0 : labor,
        contractorIssue: 0,
      },
      // Not available per-code from the catalog yet (fed by the approved
      // crawl history layer in a later phase); render guards hide these.
      historicalTrend: [],
      projections: [],
      componentTrends: {},
      supplyChain: locked ? [] : supplyChain,
    };
  };

  // Select data source based on useDemoData prop
  const displayItem = useDemoData
    ? catalogData[0]
    : adaptRealItem(item);

  if (!displayItem) {
    return (
      <div className="container-fluid bg-light min-vh-100 py-4">
        <div className="card shadow-sm">
          <div className="card-body text-center py-5">
            <MDBIcon icon="info-circle" size="3x" className="text-muted mb-3" />
            <p className="text-muted">
              {useDemoData 
                ? "Select an item from the price database above to see details."
                : "No data available. Please select an item from the table or enable demo data."}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const CostBreakdownCard = ({ itemData }) => {
    if (!itemData) return null;

    // Normalize data with fallbacks to prevent crashes
    const normalizedItem = {
      description: itemData.description || "Unknown Item",
      code: itemData.code || "N/A",
      category: itemData.category || "N/A",
      unit: itemData.unit || "unit",
      unitCost: itemData.unitCost || 0,
      location: itemData.location || { city: "Unknown", state: "N/A" },
      projectType: itemData.projectType || "N/A",
      laborType: itemData.laborType || "N/A",
      
      // Fallback for complex objects
      costBreakdown: itemData.costBreakdown || {
        rawMaterial: 0,
        manufacturing: 0,
        transportation: 0,
        distributorIssue: 0,
        equipmentCost: 0,
        laborCost: 0,
        contractorIssue: 0
      },
      historicalTrend: itemData.historicalTrend || [],
      projections: itemData.projections || [],
      componentTrends: itemData.componentTrends || {},
      supplyChain: itemData.supplyChain || []
    };

    const totalCost = normalizedItem.unitCost;
    const allHistoricalData = normalizedItem.historicalTrend;
    const projectionData = normalizedItem.projections;
    const componentTrends = normalizedItem.componentTrends;
    
    // Safe calculations for charts
    const maxHistorical = allHistoricalData.length > 0 
      ? Math.max(...allHistoricalData.map(p => p.price)) 
      : totalCost * 1.1 || 100;
      
    const maxProjection = projectionData.length > 0 
      ? Math.max(...projectionData.map(p => p.pessimistic)) 
      : maxHistorical;
      
    const maxOverall = Math.max(maxHistorical, maxProjection) || 100;

    return (
      <div className="card shadow-lg rounded-lg p-4 mt-4">
        <div className="card-body">
          {/* Header */}
          <div className="d-flex justify-content-between align-items-start mb-4">
            <div>
              <h2 className="fw-bold text-dark">{normalizedItem.description}</h2>
              <p className="text-muted mb-2">Code: {normalizedItem.code} | {normalizedItem.category}</p>
              <div className="d-flex gap-3 text-muted small">
                <span>
                  <MDBIcon icon="map-marker-alt" className="me-1" />
                  {normalizedItem.location.city}, {normalizedItem.location.state}
                </span>
                <span>•</span>
                <span>{normalizedItem.projectType} - {normalizedItem.laborType}</span>
              </div>
            </div>
            <div className="text-end">
              <div className="display-5 fw-bold text-primary">
                ${totalCost.toFixed(2)}
              </div>
              <div className="text-muted">per {normalizedItem.unit}</div>
            </div>
          </div>

          {/* Simple Explanation */}
          <div className="alert alert-info border-start border-4 border-info rounded mb-4">
            <div className="d-flex gap-3">
              <MDBIcon icon="info-circle" size="2x" className="text-info" />
              <div>
                <h5 className="fw-bold mb-2">
                  <MDBIcon icon="chart-bar" className="me-2" />
                  What This Means in Plain English
                </h5>
                <div className="mb-2">
                  <strong>Right now</strong>, {normalizedItem.description.toLowerCase()} costs{' '}
                  <strong>${totalCost.toFixed(2)} per {normalizedItem.unit}</strong>.
                </div>
                <p className="mb-2"><strong>Why this price?</strong> Out of every dollar you pay:</p>
                <div className="row g-2 small">
                  <div className="col-6">💎 ${normalizedItem.costBreakdown.rawMaterial.toFixed(2)} = Raw materials</div>
                  <div className="col-6">🏭 ${normalizedItem.costBreakdown.manufacturing.toFixed(2)} = Manufacturing</div>
                  <div className="col-6">🚚 ${normalizedItem.costBreakdown.transportation.toFixed(2)} = Transportation</div>
                  <div className="col-6">🏪 ${normalizedItem.costBreakdown.distributorIssue.toFixed(2)} = Store issue</div>
                  <div className="col-6">⚙️ ${normalizedItem.costBreakdown.equipmentCost.toFixed(2)} = Equipment</div>
                  <div className="col-6">👷 ${normalizedItem.costBreakdown.laborCost.toFixed(2)} = Labor</div>
                  <div className="col-6">🏢 ${normalizedItem.costBreakdown.contractorIssue.toFixed(2)} = Contractor</div>
                </div>
                <p className="pt-2 mb-0">
                  <strong>What happens next?</strong> Expect prices to go{' '}
                  <strong className="text-success">
                    {projectionData[0] ? ((projectionData[0].optimistic - totalCost) / totalCost * 100).toFixed(1) : "3"}%
                  </strong> to{' '}
                  <strong className="text-danger">
                    {projectionData[0] ? ((projectionData[0].pessimistic - totalCost) / totalCost * 100).toFixed(1) : "7"}%
                  </strong> higher over the next year.
                </p>
              </div>
            </div>
          </div>

          {/* Supply Chain - Accordion Style */}
          <div className="mb-4">
            <h3 className="h5 fw-bold mb-3">
              <MDBIcon icon="chart-line" className="me-2 text-primary" />
              Full Supply Chain Breakdown
            </h3>
            
            <div className="accordion" id="supplyChainAccordion">
              {normalizedItem.supplyChain.length === 0 ? (
                <div className="alert alert-secondary text-center">
                  <MDBIcon icon="exclamation-circle" className="me-2" />
                  Supply chain data unavailable for this item
                </div>
              ) : (
                normalizedItem.supplyChain.map((stage, idx) => {
                const percentage = ((stage.cost / totalCost) * 100).toFixed(1);
                const isExpanded = expandedSections[`stage-${idx}`];
                
                return (
                  <div key={idx} className="accordion-item mb-2 border rounded">
                    <h2 className="accordion-header">
                      <button 
                        className={`accordion-button ${!isExpanded ? 'collapsed' : ''} bg-light`}
                        type="button"
                        onClick={() => toggleSection(`stage-${idx}`)}
                      >
                        <div className="d-flex align-items-center justify-content-between w-100 me-3">
                          <div className="d-flex align-items-center gap-3">
                            <div className="p-2 bg-white rounded shadow-sm">
                              <MDBIcon icon={getStageIcon(stage.stage)} size="lg" />
                            </div>
                            <div>
                              <div className="fw-bold text-dark">{stage.stage}</div>
                              <small className="text-muted">{stage.entity}</small>
                            </div>
                          </div>
                          
                          <div className="d-flex align-items-center gap-3">
                            <div className="text-end">
                              <div className="fw-bold text-dark">${stage.cost.toFixed(2)}</div>
                              <small className="text-muted">{percentage}% of total</small>
                            </div>
                            
                            {stage.issue > 0 && (
                              <span className="badge bg-warning text-dark px-2 py-1">
                                +{stage.issue}% issue
                              </span>
                            )}
                            
                            {getConfidenceBadge(stage.confidence)}
                          </div>
                        </div>
                      </button>
                    </h2>
                    
                    {isExpanded && (
                      <div className="accordion-collapse collapse show">
                        <div className="accordion-body bg-white">
                          <div className="mb-3">
                            <h6 className="fw-bold text-dark mb-2">
                              <MDBIcon icon="info-circle" className="me-2" />
                              Data Sources
                            </h6>
                            <div className="d-flex flex-wrap gap-2">
                              {stage.sources.map((source, sIdx) => (
                                <span key={sIdx} className="badge bg-info text-white px-3 py-2">
                                  <MDBIcon icon="external-link-alt" size="sm" className="me-1" />
                                  {source}
                                </span>
                              ))}
                            </div>
                          </div>
                          
                          {stage.issue > 0 && (
                            <div className="alert alert-warning mb-3">
                              <div className="small">
                                <span className="fw-bold">Issue Breakdown:</span>
                                <div className="mt-1">
                                  Base Cost: ${(stage.cost / (1 + stage.issue/100)).toFixed(2)} → 
                                  Final: ${stage.cost.toFixed(2)} 
                                  <span className="text-danger fw-bold"> (+${(stage.cost - stage.cost / (1 + stage.issue/100)).toFixed(2)})</span>
                                </div>
                              </div>
                            </div>
                          )}
                          
                          {stage.details && stage.details.length > 0 && (
                            <div className="table-responsive">
                              <table className="table table-sm table-hover mb-0">
                                <thead className="table-light">
                                  <tr>
                                    <th>Code</th>
                                    <th>Description</th>
                                    <th className="text-end">Cost</th>
                                    {stage.details[0].percentage && <th className="text-end">%</th>}
                                  </tr>
                                </thead>
                                <tbody>
                                  {stage.details.map((detail, dIdx) => (
                                    <tr key={dIdx}>
                                      <td><code className="bg-light px-2 py-1 rounded">{detail.code}</code></td>
                                      <td>{detail.description}</td>
                                      <td className="text-end fw-bold">${detail.cost.toFixed(2)}</td>
                                      {detail.percentage && <td className="text-end text-muted">{detail.percentage}%</td>}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              }))}
            </div>
          </div>

          {/* Cost Summary Cards */}
          <div className="row g-3 mb-4">
            <div className="col-md-3">
              <div className="card bg-primary bg-opacity-10 border-primary border-opacity-25">
                <div className="card-body p-3">
                  <small className="text-muted">Raw Material</small>
                  <h5 className="fw-bold text-primary mb-0">
                    ${normalizedItem.costBreakdown.rawMaterial.toFixed(2)}
                  </h5>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card bg-info bg-opacity-10 border-info border-opacity-25">
                <div className="card-body p-3">
                  <small className="text-muted">Equipment</small>
                  <h5 className="fw-bold text-info mb-0">
                    ${normalizedItem.costBreakdown.equipmentCost.toFixed(2)}
                  </h5>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card bg-warning bg-opacity-10 border-warning border-opacity-25">
                <div className="card-body p-3">
                  <small className="text-muted">Labor</small>
                  <h5 className="fw-bold text-warning mb-0">
                    ${normalizedItem.costBreakdown.laborCost.toFixed(2)}
                  </h5>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card bg-success bg-opacity-10 border-success border-opacity-25">
                <div className="card-body p-3">
                  <small className="text-muted">Contractor</small>
                  <h5 className="fw-bold text-success mb-0">
                    ${normalizedItem.costBreakdown.contractorIssue.toFixed(2)}
                  </h5>
                </div>
              </div>
            </div>
          </div>


          {/* MAIN PRICE HISTORY + PROJECTIONS CHART */}
          {(allHistoricalData.length > 0 || projectionData.length > 0) && (
            <div className="mb-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h3 className="h5 fw-bold d-flex align-items-center gap-2">
                  <MDBIcon icon="chart-line" className="text-primary" />
                  Price History and Future Projections
                </h3>
                <div className="d-flex gap-3 small">
                  <div className="d-flex align-items-center gap-2">
                    <div style={{width: '12px', height: '12px', backgroundColor: '#0d6efd', borderRadius: '2px'}}></div>
                    <span>Actual</span>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <div style={{width: '12px', height: '12px', backgroundColor: '#198754', borderRadius: '2px'}}></div>
                    <span>Best</span>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <div style={{width: '12px', height: '12px', backgroundColor: '#ffc107', borderRadius: '2px'}}></div>
                    <span>Likely</span>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <div style={{width: '12px', height: '12px', backgroundColor: '#dc3545', borderRadius: '2px'}}></div>
                    <span>Worst</span>
                  </div>
                </div>
              </div>
              
              <div className="card border">
                <div className="card-body p-4" style={{background: 'linear-gradient(to bottom, #f8f9fa, white)'}}>
                  <div style={{position: 'relative', height: '400px'}}>
                    {/* Grid lines */}
                    <div className="position-absolute w-100 h-100 d-flex flex-column justify-content-between">
                      {[0, 1, 2, 3, 4].map(i => (
                        <div key={i} className="border-top border-secondary border-opacity-25 w-100"></div>
                      ))}
                    </div>
                    
                    {/* Price labels */}
                    <div className="position-absolute top-0 bottom-0 d-flex flex-column justify-content-between small text-muted" style={{left: '-48px'}}>
                      {[4, 3, 2, 1, 0].map(i => {
                        const price = maxOverall * (i / 4);
                        return <div key={i}>${price.toFixed(0)}</div>;
                      })}
                    </div>
                    
                    {/* Chart using SVG */}
                    <div className="position-relative h-100" style={{paddingLeft: '16px'}}>
                      <svg className="w-100 h-100" style={{overflow: 'visible'}}>
                        {/* Draw historical line */}
                        {allHistoricalData.map((point, idx) => {
                          const nextPoint = allHistoricalData[idx + 1];
                          const x = ((idx + 0.5) / (allHistoricalData.length + projectionData.length)) * 100;
                          const y = 100 - ((point.price / maxOverall) * 90);
                          const hasEvent = point.events && point.events.length > 0;
                          const isToday = point.isToday;
                          
                          return (
                            <g key={`hist-${idx}`}>
                              {/* Line to next point */}
                              {nextPoint && (
                                <line
                                  x1={`${x}%`}
                                  y1={`${y}%`}
                                  x2={`${((idx + 1.5) / (allHistoricalData.length + projectionData.length)) * 100}%`}
                                  y2={`${100 - ((nextPoint.price / maxOverall) * 90)}%`}
                                  stroke="#0d6efd"
                                  strokeWidth="3"
                                />
                              )}
                              
                              {/* Event marker */}
                              {hasEvent && (
                                <circle
                                  cx={`${x}%`}
                                  cy={`${y - 5}%`}
                                  r="4"
                                  fill={point.events[0].type === 'negative' ? '#dc3545' : point.events[0].type === 'positive' ? '#198754' : '#ffc107'}
                                />
                              )}
                              
                              {/* Data point */}
                              <circle
                                cx={`${x}%`}
                                cy={`${y}%`}
                                r={isToday ? "8" : "5"}
                                fill={isToday ? "#ffc107" : "#0d6efd"}
                                stroke="white"
                                strokeWidth="2"
                              >
                                <title>${point.price.toFixed(2)} - {point.date}</title>
                              </circle>
                              
                              {/* TODAY label */}
                              {isToday && (
                                <>
                                  <circle
                                    cx={`${x}%`}
                                    cy={`${y}%`}
                                    r="12"
                                    fill="none"
                                    stroke="#ffc107"
                                    strokeWidth="2"
                                  />
                                  <text
                                    x={`${x}%`}
                                    y={`${y - 8}%`}
                                    textAnchor="middle"
                                    fontSize="11"
                                    fontWeight="bold"
                                    fill="#856404"
                                  >
                                    TODAY
                                  </text>
                                </>
                              )}
                            </g>
                          );
                        })}
                        
                        {/* Draw projection lines */}
                        {projectionData.map((proj, idx) => {
                          const prevPoint = idx === 0 
                            ? allHistoricalData[allHistoricalData.length - 1] 
                            : projectionData[idx - 1];
                          
                          const x = ((allHistoricalData.length + idx + 0.5) / (allHistoricalData.length + projectionData.length)) * 100;
                          const prevX = idx === 0 
                            ? ((allHistoricalData.length - 0.5) / (allHistoricalData.length + projectionData.length)) * 100
                            : ((allHistoricalData.length + idx - 0.5) / (allHistoricalData.length + projectionData.length)) * 100;
                          
                          const yOptimistic = 100 - ((proj.optimistic / maxOverall) * 90);
                          const yRealistic = 100 - ((proj.realistic / maxOverall) * 90);
                          const yPessimistic = 100 - ((proj.pessimistic / maxOverall) * 90);
                          
                          const prevY = idx === 0 
                            ? 100 - ((prevPoint.price / maxOverall) * 90)
                            : 100 - ((prevPoint.realistic / maxOverall) * 90);
                          
                          const nextProj = projectionData[idx + 1];
                          
                          return (
                            <g key={`proj-${idx}`}>
                              {/* Lines from previous point */}
                              <line
                                x1={`${prevX}%`}
                                y1={`${prevY}%`}
                                x2={`${x}%`}
                                y2={`${yOptimistic}%`}
                                stroke="#198754"
                                strokeWidth="2"
                                strokeDasharray="5,5"
                              />
                              <line
                                x1={`${prevX}%`}
                                y1={`${prevY}%`}
                                x2={`${x}%`}
                                y2={`${yRealistic}%`}
                                stroke="#ffc107"
                                strokeWidth="3"
                                strokeDasharray="5,5"
                              />
                              <line
                                x1={`${prevX}%`}
                                y1={`${prevY}%`}
                                x2={`${x}%`}
                                y2={`${yPessimistic}%`}
                                stroke="#dc3545"
                                strokeWidth="2"
                                strokeDasharray="5,5"
                              />
                              
                              {/* Lines to next projection */}
                              {nextProj && (
                                <>
                                  <line
                                    x1={`${x}%`}
                                    y1={`${yOptimistic}%`}
                                    x2={`${((allHistoricalData.length + idx + 1.5) / (allHistoricalData.length + projectionData.length)) * 100}%`}
                                    y2={`${100 - ((nextProj.optimistic / maxOverall) * 90)}%`}
                                    stroke="#198754"
                                    strokeWidth="2"
                                    strokeDasharray="5,5"
                                  />
                                  <line
                                    x1={`${x}%`}
                                    y1={`${yRealistic}%`}
                                    x2={`${((allHistoricalData.length + idx + 1.5) / (allHistoricalData.length + projectionData.length)) * 100}%`}
                                    y2={`${100 - ((nextProj.realistic / maxOverall) * 90)}%`}
                                    stroke="#ffc107"
                                    strokeWidth="3"
                                    strokeDasharray="5,5"
                                  />
                                  <line
                                    x1={`${x}%`}
                                    y1={`${yPessimistic}%`}
                                    x2={`${((allHistoricalData.length + idx + 1.5) / (allHistoricalData.length + projectionData.length)) * 100}%`}
                                    y2={`${100 - ((nextProj.pessimistic / maxOverall) * 90)}%`}
                                    stroke="#dc3545"
                                    strokeWidth="2"
                                    strokeDasharray="5,5"
                                  />
                                </>
                              )}
                              
                              {/* Data points */}
                              <circle
                                cx={`${x}%`}
                                cy={`${yPessimistic}%`}
                                r="4"
                                fill="#dc3545"
                                stroke="white"
                                strokeWidth="2"
                              >
                                <title>Worst: ${proj.pessimistic.toFixed(2)}</title>
                              </circle>
                              <circle
                                cx={`${x}%`}
                                cy={`${yRealistic}%`}
                                r="5"
                                fill="#ffc107"
                                stroke="white"
                                strokeWidth="2"
                              >
                                <title>Likely: ${proj.realistic.toFixed(2)}</title>
                              </circle>
                              <circle
                                cx={`${x}%`}
                                cy={`${yOptimistic}%`}
                                r="4"
                                fill="#198754"
                                stroke="white"
                                strokeWidth="2"
                              >
                                <title>Best: ${proj.optimistic.toFixed(2)}</title>
                              </circle>
                            </g>
                          );
                        })}
                        
                        {/* Date labels */}
                        {allHistoricalData.map((point, idx) => {
                          const x = ((idx + 0.5) / (allHistoricalData.length + projectionData.length)) * 100;
                          return (
                            <text
                              key={`label-hist-${idx}`}
                              x={`${x}%`}
                              y="105%"
                              textAnchor="middle"
                              fontSize="10"
                              fill={point.isToday ? "#1e40af" : "#6c757d"}
                              fontWeight={point.isToday ? "bold" : "normal"}
                            >
                              {point.date}
                            </text>
                          );
                        })}
                        {projectionData.map((proj, idx) => {
                          const x = ((allHistoricalData.length + idx + 0.5) / (allHistoricalData.length + projectionData.length)) * 100;
                          return (
                            <text
                              key={`label-proj-${idx}`}
                              x={`${x}%`}
                              y="105%"
                              textAnchor="middle"
                              fontSize="10"
                              fill="#6c757d"
                              fontWeight="bold"
                            >
                              {proj.date}
                            </text>
                          );
                        })}
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* SCENARIO ANALYSIS */}
          {projectionData.length > 0 && projectionData[0].scenarios && projectionData[0].scenarios.length > 0 && (
            <div className="mb-4">
              <h3 className="h5 fw-bold mb-3 d-flex align-items-center gap-2">
                <MDBIcon icon="exclamation-triangle" className="text-warning" />
                What Could Change These Prices?
              </h3>
              
              <div className="row g-3">
                {projectionData[0].scenarios.map((scenario, idx) => {
                  const probabilityNum = parseInt(scenario.probability);
                  const isHighProb = probabilityNum >= 60;
                  
                  return (
                    <div key={idx} className="col-md-6">
                      <div className={`card ${isHighProb ? 'border-warning border-2' : 'border'}`}>
                        <div className={`card-body ${isHighProb ? 'bg-warning bg-opacity-10' : ''}`}>
                          <div className="d-flex gap-3">
                            <div className={`p-2 rounded ${isHighProb ? 'bg-warning bg-opacity-25' : 'bg-light'}`}>
                              <MDBIcon icon={isHighProb ? "bolt" : "globe"} size="lg" className={isHighProb ? 'text-warning' : 'text-muted'} />
                            </div>
                            <div className="flex-grow-1">
                              <div className="d-flex justify-content-between align-items-start mb-2">
                                <h6 className="fw-bold mb-0">{scenario.condition}</h6>
                                <span className={`badge ${isHighProb ? 'bg-warning text-dark' : 'bg-secondary'}`}>
                                  {scenario.probability} likely
                                </span>
                              </div>
                              <div className="h4 fw-bold text-dark mb-1">
                                {scenario.impact}
                              </div>
                              <small className="text-muted">
                                {isHighProb ? '?? Very likely to happen' : '?? Might happen'}
                              </small>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* COMPONENT TRENDS - 6 mini charts */}
          {Object.keys(componentTrends).length > 0 && (
            <div className="mb-4">
              <h3 className="h5 fw-bold mb-3 d-flex align-items-center gap-2">
                <MDBIcon icon="chart-area" className="text-info" />
                What is Driving Price Changes?
              </h3>
              
              <div className="row g-3">
                {/* Raw Material Chart */}
                {componentTrends.rawMaterial && (
                  <div className="col-md-6">
                    <div className="card border-primary border-opacity-25">
                      <div className="card-body p-3" style={{background: 'linear-gradient(135deg, #cfe2ff 0%, #e7f1ff 100%)'}}>
                        <div className="d-flex align-items-center gap-2 mb-3">
                          <MDBIcon icon="cube" className="text-primary" />
                          <h6 className="fw-bold mb-0 text-dark">Raw Materials</h6>
                        </div>
                        <div style={{height: '128px', position: 'relative'}}>
                          <svg className="w-100 h-100" style={{overflow: 'visible'}}>
                            {componentTrends.rawMaterial.map((point, idx) => {
                              const maxPrice = Math.max(...componentTrends.rawMaterial.map(p => p.price));
                              const minPrice = Math.min(...componentTrends.rawMaterial.map(p => p.price));
                              const range = maxPrice - minPrice;
                              const padding = range * 0.1;
                              const x = (idx / (componentTrends.rawMaterial.length - 1)) * 100;
                              const y = 100 - (((point.price - minPrice + padding) / (range + 2 * padding)) * 90);
                              const nextPoint = componentTrends.rawMaterial[idx + 1];
                              const isToday = point.isToday;
                              
                              return (
                                <g key={idx}>
                                  {nextPoint && (
                                    <line
                                      x1={`${x}%`}
                                      y1={`${y}%`}
                                      x2={`${((idx + 1) / (componentTrends.rawMaterial.length - 1)) * 100}%`}
                                      y2={`${100 - (((nextPoint.price - minPrice + padding) / (range + 2 * padding)) * 90)}%`}
                                      stroke={nextPoint.isHistory ? "#0d6efd" : "#9ec5fe"}
                                      strokeWidth="2"
                                      strokeDasharray={nextPoint.isHistory ? "0" : "4"}
                                    />
                                  )}
                                  <circle
                                    cx={`${x}%`}
                                    cy={`${y}%`}
                                    r={point.event ? "5" : isToday ? "6" : "3"}
                                    fill={point.event ? "#dc3545" : isToday ? "#ffc107" : point.isHistory ? "#0d6efd" : "#9ec5fe"}
                                    stroke="white"
                                    strokeWidth="2"
                                  >
                                    <title>${point.price.toFixed(2)} - {point.date}{point.event ? ` (${point.event})` : ''}</title>
                                  </circle>
                                  {isToday && (
                                    <text x={`${x}%`} y={`${y - 3}%`} textAnchor="middle" fontSize="9" fill="#856404" fontWeight="bold">
                                      NOW
                                    </text>
                                  )}
                                </g>
                              );
                            })}
                          </svg>
                        </div>
                        <div className="d-flex justify-content-between align-items-center mt-2 small text-muted">
                          <span>
                            <strong className="text-primary">
                              ${componentTrends.rawMaterial.find(p => p.isToday)?.price.toFixed(2) || componentTrends.rawMaterial[Math.floor(componentTrends.rawMaterial.length / 2)].price.toFixed(2)}
                            </strong> now
                          </span>
                          <span style={{fontSize: '0.75rem'}}>? Proj: ${componentTrends.rawMaterial[componentTrends.rawMaterial.length - 1].price.toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Equipment Chart */}
                {componentTrends.equipment && (
                  <div className="col-md-6">
                    <div className="card border-info border-opacity-25">
                      <div className="card-body p-3" style={{background: 'linear-gradient(135deg, #cff4fc 0%, #e7f9fd 100%)'}}>
                        <div className="d-flex align-items-center gap-2 mb-3">
                          <MDBIcon icon="wrench" className="text-info" />
                          <h6 className="fw-bold mb-0 text-dark">Equipment Costs</h6>
                        </div>
                        <div style={{height: '128px', position: 'relative'}}>
                          <svg className="w-100 h-100" style={{overflow: 'visible'}}>
                            {componentTrends.equipment.map((point, idx) => {
                              const maxPrice = Math.max(...componentTrends.equipment.map(p => p.price));
                              const minPrice = Math.min(...componentTrends.equipment.map(p => p.price));
                              const range = maxPrice - minPrice;
                              const padding = range * 0.1;
                              const x = (idx / (componentTrends.equipment.length - 1)) * 100;
                              const y = 100 - (((point.price - minPrice + padding) / (range + 2 * padding)) * 90);
                              const nextPoint = componentTrends.equipment[idx + 1];
                              const isToday = point.isToday;
                              
                              return (
                                <g key={idx}>
                                  {nextPoint && (
                                    <line
                                      x1={`${x}%`}
                                      y1={`${y}%`}
                                      x2={`${((idx + 1) / (componentTrends.equipment.length - 1)) * 100}%`}
                                      y2={`${100 - (((nextPoint.price - minPrice + padding) / (range + 2 * padding)) * 90)}%`}
                                      stroke={nextPoint.isHistory ? "#0dcaf0" : "#9eeaf9"}
                                      strokeWidth="2"
                                      strokeDasharray={nextPoint.isHistory ? "0" : "4"}
                                    />
                                  )}
                                  <circle
                                    cx={`${x}%`}
                                    cy={`${y}%`}
                                    r={isToday ? "6" : "3"}
                                    fill={isToday ? "#ffc107" : point.isHistory ? "#0dcaf0" : "#9eeaf9"}
                                    stroke="white"
                                    strokeWidth="2"
                                  >
                                    <title>${point.price.toFixed(2)} - {point.date}</title>
                                  </circle>
                                  {isToday && (
                                    <text x={`${x}%`} y={`${y - 3}%`} textAnchor="middle" fontSize="9" fill="#856404" fontWeight="bold">
                                      NOW
                                    </text>
                                  )}
                                </g>
                              );
                            })}
                          </svg>
                        </div>
                        <div className="d-flex justify-content-between align-items-center mt-2 small text-muted">
                          <span>
                            <strong className="text-info">
                              ${componentTrends.equipment.find(p => p.isToday)?.price.toFixed(2) || componentTrends.equipment[Math.floor(componentTrends.equipment.length / 2)].price.toFixed(2)}
                            </strong> now
                          </span>
                          <span style={{fontSize: '0.75rem'}}>? Proj: ${componentTrends.equipment[componentTrends.equipment.length - 1].price.toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Labor Chart */}
                {componentTrends.labor && (
                  <div className="col-md-6">
                    <div className="card border-warning border-opacity-25">
                      <div className="card-body p-3" style={{background: 'linear-gradient(135deg, #fff3cd 0%, #fffaeb 100%)'}}>
                        <div className="d-flex align-items-center gap-2 mb-3">
                          <MDBIcon icon="hard-hat" className="text-warning" />
                          <h6 className="fw-bold mb-0 text-dark">Labor Costs</h6>
                        </div>
                        <div style={{height: '128px', position: 'relative'}}>
                          <svg className="w-100 h-100" style={{overflow: 'visible'}}>
                            {componentTrends.labor.map((point, idx) => {
                              const maxPrice = Math.max(...componentTrends.labor.map(p => p.price));
                              const minPrice = Math.min(...componentTrends.labor.map(p => p.price));
                              const range = maxPrice - minPrice;
                              const padding = range * 0.1;
                              const x = (idx / (componentTrends.labor.length - 1)) * 100;
                              const y = 100 - (((point.price - minPrice + padding) / (range + 2 * padding)) * 90);
                              const nextPoint = componentTrends.labor[idx + 1];
                              const isToday = point.isToday;
                              
                              return (
                                <g key={idx}>
                                  {nextPoint && (
                                    <line
                                      x1={`${x}%`}
                                      y1={`${y}%`}
                                      x2={`${((idx + 1) / (componentTrends.labor.length - 1)) * 100}%`}
                                      y2={`${100 - (((nextPoint.price - minPrice + padding) / (range + 2 * padding)) * 90)}%`}
                                      stroke={nextPoint.isHistory ? "#ffc107" : "#ffe69c"}
                                      strokeWidth="2"
                                      strokeDasharray={nextPoint.isHistory ? "0" : "4"}
                                    />
                                  )}
                                  <circle
                                    cx={`${x}%`}
                                    cy={`${y}%`}
                                    r={point.event ? "5" : isToday ? "6" : "3"}
                                    fill={point.event ? "#198754" : isToday ? "#dc3545" : point.isHistory ? "#ffc107" : "#ffe69c"}
                                    stroke="white"
                                    strokeWidth="2"
                                  >
                                    <title>${point.price.toFixed(2)} - {point.date}{point.event ? ` (${point.event})` : ''}</title>
                                  </circle>
                                  {isToday && (
                                    <text x={`${x}%`} y={`${y - 3}%`} textAnchor="middle" fontSize="9" fill="#856404" fontWeight="bold">
                                      NOW
                                    </text>
                                  )}
                                </g>
                              );
                            })}
                          </svg>
                        </div>
                        <div className="d-flex justify-content-between align-items-center mt-2 small text-muted">
                          <span>
                            <strong className="text-warning">
                              ${componentTrends.labor.find(p => p.isToday)?.price.toFixed(2) || componentTrends.labor[Math.floor(componentTrends.labor.length / 2)].price.toFixed(2)}
                            </strong> now
                          </span>
                          <span style={{fontSize: '0.75rem'}}>? Proj: ${componentTrends.labor[componentTrends.labor.length - 1].price.toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Transportation Chart */}
                {componentTrends.transportation && (
                  <div className="col-md-6">
                    <div className="card border-success border-opacity-25">
                      <div className="card-body p-3" style={{background: 'linear-gradient(135deg, #d1e7dd 0%, #e8f5e9 100%)'}}>
                        <div className="d-flex align-items-center gap-2 mb-3">
                          <MDBIcon icon="truck" className="text-success" />
                          <h6 className="fw-bold mb-0 text-dark">Transportation</h6>
                        </div>
                        <div style={{height: '128px', position: 'relative'}}>
                          <svg className="w-100 h-100" style={{overflow: 'visible'}}>
                            {componentTrends.transportation.map((point, idx) => {
                              const maxPrice = Math.max(...componentTrends.transportation.map(p => p.price));
                              const minPrice = Math.min(...componentTrends.transportation.map(p => p.price));
                              const range = maxPrice - minPrice;
                              const padding = range * 0.1;
                              const x = (idx / (componentTrends.transportation.length - 1)) * 100;
                              const y = 100 - (((point.price - minPrice + padding) / (range + 2 * padding)) * 90);
                              const nextPoint = componentTrends.transportation[idx + 1];
                              const isToday = point.isToday;
                              
                              return (
                                <g key={idx}>
                                  {nextPoint && (
                                    <line
                                      x1={`${x}%`}
                                      y1={`${y}%`}
                                      x2={`${((idx + 1) / (componentTrends.transportation.length - 1)) * 100}%`}
                                      y2={`${100 - (((nextPoint.price - minPrice + padding) / (range + 2 * padding)) * 90)}%`}
                                      stroke={nextPoint.isHistory ? "#198754" : "#a3cfbb"}
                                      strokeWidth="2"
                                      strokeDasharray={nextPoint.isHistory ? "0" : "4"}
                                    />
                                  )}
                                  <circle
                                    cx={`${x}%`}
                                    cy={`${y}%`}
                                    r={point.event ? "5" : isToday ? "6" : "3"}
                                    fill={point.event ? "#dc3545" : isToday ? "#ffc107" : point.isHistory ? "#198754" : "#a3cfbb"}
                                    stroke="white"
                                    strokeWidth="2"
                                  >
                                    <title>${point.price.toFixed(2)} - {point.date}{point.event ? ` (${point.event})` : ''}</title>
                                  </circle>
                                  {isToday && (
                                    <text x={`${x}%`} y={`${y - 3}%`} textAnchor="middle" fontSize="9" fill="#856404" fontWeight="bold">
                                      NOW
                                    </text>
                                  )}
                                </g>
                              );
                            })}
                          </svg>
                        </div>
                        <div className="d-flex justify-content-between align-items-center mt-2 small text-muted">
                          <span>
                            <strong className="text-success">
                              ${componentTrends.transportation.find(p => p.isToday)?.price.toFixed(2) || componentTrends.transportation[Math.floor(componentTrends.transportation.length / 2)].price.toFixed(2)}
                            </strong> now
                          </span>
                          <span style={{fontSize: '0.75rem'}}>? Proj: ${componentTrends.transportation[componentTrends.transportation.length - 1].price.toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Distribution Chart */}
                {componentTrends.distribution && (
                  <div className="col-md-6">
                    <div className="card border-secondary border-opacity-25">
                      <div className="card-body p-3" style={{background: 'linear-gradient(135deg, #e2e3e5 0%, #f0f0f0 100%)'}}>
                        <div className="d-flex align-items-center gap-2 mb-3">
                          <MDBIcon icon="store" className="text-secondary" />
                          <h6 className="fw-bold mb-0 text-dark">Store Issues</h6>
                        </div>
                        <div style={{height: '128px', position: 'relative'}}>
                          <svg className="w-100 h-100" style={{overflow: 'visible'}}>
                            {componentTrends.distribution.map((point, idx) => {
                              const maxPrice = Math.max(...componentTrends.distribution.map(p => p.price));
                              const minPrice = Math.min(...componentTrends.distribution.map(p => p.price));
                              const range = maxPrice - minPrice;
                              const padding = range * 0.1;
                              const x = (idx / (componentTrends.distribution.length - 1)) * 100;
                              const y = 100 - (((point.price - minPrice + padding) / (range + 2 * padding)) * 90);
                              const nextPoint = componentTrends.distribution[idx + 1];
                              const isToday = point.isToday;
                              
                              return (
                                <g key={idx}>
                                  {nextPoint && (
                                    <line
                                      x1={`${x}%`}
                                      y1={`${y}%`}
                                      x2={`${((idx + 1) / (componentTrends.distribution.length - 1)) * 100}%`}
                                      y2={`${100 - (((nextPoint.price - minPrice + padding) / (range + 2 * padding)) * 90)}%`}
                                      stroke={nextPoint.isHistory ? "#6c757d" : "#adb5bd"}
                                      strokeWidth="2"
                                      strokeDasharray={nextPoint.isHistory ? "0" : "4"}
                                    />
                                  )}
                                  <circle
                                    cx={`${x}%`}
                                    cy={`${y}%`}
                                    r={isToday ? "6" : "3"}
                                    fill={isToday ? "#ffc107" : point.isHistory ? "#6c757d" : "#adb5bd"}
                                    stroke="white"
                                    strokeWidth="2"
                                  >
                                    <title>${point.price.toFixed(2)} - {point.date}</title>
                                  </circle>
                                  {isToday && (
                                    <text x={`${x}%`} y={`${y - 3}%`} textAnchor="middle" fontSize="9" fill="#856404" fontWeight="bold">
                                      NOW
                                    </text>
                                  )}
                                </g>
                              );
                            })}
                          </svg>
                        </div>
                        <div className="d-flex justify-content-between align-items-center mt-2 small text-muted">
                          <span>
                            <strong className="text-secondary">
                              ${componentTrends.distribution.find(p => p.isToday)?.price.toFixed(2) || componentTrends.distribution[Math.floor(componentTrends.distribution.length / 2)].price.toFixed(2)}
                            </strong> now
                          </span>
                          <span style={{fontSize: '0.75rem'}}>? Proj: ${componentTrends.distribution[componentTrends.distribution.length - 1].price.toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Contractor Chart */}
                {componentTrends.contractor && (
                  <div className="col-md-6">
                    <div className="card border-dark border-opacity-25">
                      <div className="card-body p-3" style={{background: 'linear-gradient(135deg, #dee2e6 0%, #f8f9fa 100%)'}}>
                        <div className="d-flex align-items-center gap-2 mb-3">
                          <MDBIcon icon="building" className="text-dark" />
                          <h6 className="fw-bold mb-0 text-dark">Contractor Costs</h6>
                        </div>
                        <div style={{height: '128px', position: 'relative'}}>
                          <svg className="w-100 h-100" style={{overflow: 'visible'}}>
                            {componentTrends.contractor.map((point, idx) => {
                              const maxPrice = Math.max(...componentTrends.contractor.map(p => p.price));
                              const minPrice = Math.min(...componentTrends.contractor.map(p => p.price));
                              const range = maxPrice - minPrice;
                              const padding = range * 0.1;
                              const x = (idx / (componentTrends.contractor.length - 1)) * 100;
                              const y = 100 - (((point.price - minPrice + padding) / (range + 2 * padding)) * 90);
                              const nextPoint = componentTrends.contractor[idx + 1];
                              const isToday = point.isToday;
                              
                              return (
                                <g key={idx}>
                                  {nextPoint && (
                                    <line
                                      x1={`${x}%`}
                                      y1={`${y}%`}
                                      x2={`${((idx + 1) / (componentTrends.contractor.length - 1)) * 100}%`}
                                      y2={`${100 - (((nextPoint.price - minPrice + padding) / (range + 2 * padding)) * 90)}%`}
                                      stroke={nextPoint.isHistory ? "#212529" : "#999"}
                                      strokeWidth="2"
                                      strokeDasharray={nextPoint.isHistory ? "0" : "4"}
                                    />
                                  )}
                                  <circle
                                    cx={`${x}%`}
                                    cy={`${y}%`}
                                    r={isToday ? "6" : "3"}
                                    fill={isToday ? "#ffc107" : point.isHistory ? "#212529" : "#999"}
                                    stroke="white"
                                    strokeWidth="2"
                                  >
                                    <title>${point.price.toFixed(2)} - {point.date}</title>
                                  </circle>
                                  {isToday && (
                                    <text x={`${x}%`} y={`${y - 3}%`} textAnchor="middle" fontSize="9" fill="#856404" fontWeight="bold">
                                      NOW
                                    </text>
                                  )}
                                </g>
                              );
                            })}
                          </svg>
                        </div>
                        <div className="d-flex justify-content-between align-items-center mt-2 small text-muted">
                          <span>
                            <strong className="text-dark">
                              ${componentTrends.contractor.find(p => p.isToday)?.price.toFixed(2) || componentTrends.contractor[Math.floor(componentTrends.contractor.length / 2)].price.toFixed(2)}
                            </strong> now
                          </span>
                          <span style={{fontSize: '0.75rem'}}>? Proj: ${componentTrends.contractor[componentTrends.contractor.length - 1].price.toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Data availability note */}
          {(!allHistoricalData.length && !projectionData.length && !Object.keys(componentTrends).length) && (
            <div className="alert alert-info">
              <MDBIcon icon="info-circle" className="me-2" />
              {useDemoData
                ? "Historical trend, projection, and component trend data not available for this item."
                : "Price history and projections for this item are populated from the daily market-data feed as approved data accrues — not yet available for this code."}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="container-fluid bg-light min-vh-100 p-4">
      <div className="container-xl">
        {/* Header */}
        {useDemoData && (
          <>
            <div className="mb-4">
              <h1 className="display-6 fw-bold text-dark mb-2">
                Construction Cost Transparency Dashboard
              </h1>
              <p className="text-muted">
                Complete supply chain breakdown with sourced explanations for every cost component
              </p>
            </div>

          </>
        )}

        {/* Cost Breakdown */}
        <CostBreakdownCard itemData={displayItem} />
      </div>
    </div>
  );
};

export default ConstructionCostDashboard;
